import { FC, useEffect, useMemo, useState } from 'react';
import { useField } from 'formik';
import cx from 'classnames';

import InputSearch from 'shared/components/Inputs/InputSearch';

import { API } from 'core/API';

import { ReactComponent as ArrowIcon } from 'assets/image/dashboard/smart-grop-list/arrow-icon.svg';
import { ReactComponent as Checked } from 'assets/image/dashboard/smart-grop-list/checked.svg';
import { ReactComponent as Unchecked } from 'assets/image/dashboard/smart-grop-list/unchecked.svg';

import { SearchOrganizationByAdminRequest } from '@joc/api-gateway';
import { SearchOrganisationByAdminResponse } from '@joc/api-gateway/lib/api-client';

import styles from './SmartGroupList.module.scss';

const SmartGroupList: FC<{ name: string }> = ({ name }) => {
	const [orgsResponse, setOrganizations] = useState<Array<SearchOrganisationByAdminResponse>>([]);

	const [field, , helpers] = useField<Array<number>>(name);

	const [isOpen, setIsOpen] = useState(true);
	const [isExpanded, setIsExpanded] = useState(false);
	const [search, setSearch] = useState('');

	useEffect(() => {
		const fetchOrganizations = async () => {
			try {
				const res = await API.searchOrganizationsByAdmin(SearchOrganizationByAdminRequest.fromJS({}));
				setOrganizations(res?.records || []);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('Error fetching smart groups:', error);
			}
		};

		fetchOrganizations().finally();
	}, []);

	const organizations = useMemo(() => {
		if (!orgsResponse) return [];
		if (search) {
			return orgsResponse.filter(item => item.organizationName.toLowerCase().includes(search.toLowerCase()));
		}
		return orgsResponse;
	}, [orgsResponse, search]);

	const searchChangeHandler = (value: string) => setSearch(value);

	const toggleChangeHandler = (groupId: number) => {
		if (field.value.includes(groupId)) {
			helpers.setValue(field.value.filter(id => id !== groupId)).finally();
		} else {
			helpers.setValue([...field.value, groupId]).finally();
		}
	};

	return (
		<div className={styles.smartGroupList}>
			<div className={styles.smartGroupList__label} onClick={() => setIsOpen(!isOpen)}>
				<div className={styles.smartGroupList__label_title}>
					<span className={styles.smartGroupList__label_title_text}>Organizations</span>
					{field.value.length ? (
						<span className={styles.smartGroupList__label_title_counter}>{field.value.length}</span>
					) : null}
				</div>
				<ArrowIcon
					className={cx(styles.smartGroupList__label_icon, {
						[styles.smartGroupList__label_icon_active]: isOpen,
					})}
				/>
			</div>

			<div className={cx(styles.smartGroupList__content, { [styles.smartGroupList__content_open]: isOpen })}>
				<div className={styles.smartGroupList__content_search_container}>
					<InputSearch
						placeholder="Search..."
						parentClass={cx(styles.smartGroupList__content_search, {
							[styles.smartGroupList__content_search_error]: search.length && !organizations.length,
						})}
						changeHandler={searchChangeHandler}
						isClearSearch={false}
					/>
					{search.length && !organizations.length ? <span>No filters</span> : null}
				</div>
				<div
					className={cx(styles.smartGroupList__content_list, {
						[styles.smartGroupList__content_list_expanded]: isExpanded,
					})}
				>
					{organizations?.map(item => (
						<div
							key={item.id}
							className={cx(styles.smartGroupList__content_list_item, {
								[styles.smartGroupList__content_list_item_active]: field.value.includes(
									Number(item.id)
								),
							})}
							onClick={() => toggleChangeHandler(Number(item.id))}
						>
							{field.value.includes(Number(item.id)) ? <Checked /> : <Unchecked />}
							<span>{item.organizationName}</span>
						</div>
					))}
				</div>
				{organizations.length && !search ? (
					<div
						className={styles.smartGroupList__content__expand}
						onClick={() => setIsExpanded(prevState => !prevState)}
					>
						<span className={styles.smartGroupList__content__expand_title}>
							{!isExpanded ? 'Expand' : 'Collapse'}
						</span>
					</div>
				) : null}
			</div>
		</div>
	);
};

export default SmartGroupList;
